/*
 * assets/collection.js — Boostly collection page.
 * Replaces theme.Filters (theme.js): tag select → navigate, sort select → ?sort_by=, _resizeSelect.
 * Adds Shopify storefront filtering: the filter drawer form re-renders the section via
 * ?section_id= (Section Rendering API) without a full page reload, keeps history in sync.
 *   <boostly-nav-select>          select that navigates (tag filter) or sets a query param (sort)
 *   <boostly-collection-filters>  filter form + active filter pills + results swapping
 */
(function () {
  'use strict';
  const define = (n, c) => { if (!customElements.get(n)) customElements.define(n, c); };

  class NavSelect extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      if (!this.select) return;
      this.select.addEventListener('change', () => {
        const param = this.dataset.param;
        if (!param) { window.location.href = this.select.value + window.location.search.replace(/([?&])page=\d+&?/, '$1'); return; }
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        if (this.select.value === this.dataset.default) url.searchParams.delete(param); else url.searchParams.set(param, this.select.value);
        const host = this.closest('boostly-collection-filters');
        if (host && host.render) host.render(url.toString()); else window.location.href = url.toString();
      });
    }
  }
  define('boostly-nav-select', NavSelect);

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.bind();
      // Always resync on popstate: the state marker is only set on entries *we* pushed, but
      // Back can also land on the page's original (pre-ajax) load, whose entry has state=null.
      // If we required the marker there, the DOM would stay stuck on the last ajax result while
      // the address bar reverted to the original URL.
      window.addEventListener('popstate', this._pop = () => this.render(window.location.href, false));
    }
    disconnectedCallback() { window.removeEventListener('popstate', this._pop); }
    bind() {
      this.form = this.querySelector('[data-filters-form]');
      if (this.form) {
        this.form.addEventListener('submit', (e) => { e.preventDefault(); this.render(this.formUrl()); });
        this.form.addEventListener('change', Theme.debounce((e) => { if (e.target.type === 'checkbox') this.render(this.formUrl(), true, false); }, 400));
      }
      this.addEventListener('click', this._click = (e) => {
        const a = e.target.closest('[data-filter-remove], [data-filters-clear], .pagination a');
        if (!a || !this.contains(a)) return;
        e.preventDefault();
        this.render(a.href, true, !a.closest('.pagination'));
        if (a.closest('.pagination')) this.scrollIntoView({ behavior: 'smooth' });
      });
    }
    formUrl() {
      const fd = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) { if (v !== '') params.append(k, v); }
      const url = new URL(this.dataset.collectionUrl || window.location.pathname, window.location.origin);
      url.search = params.toString();
      return url.toString();
    }
    async render(url, push = true, keepDrawer = true) {
      const results = this.querySelector('[data-collection-results]');
      if (results) results.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');
      try {
        const doc = await Theme.fetchSection(this.sectionId, url);
        const fresh = doc.querySelector('boostly-collection-filters');
        if (!fresh) { window.location.href = url; return; }
        const drawerOpen = this.querySelector('[data-drawer].is-open');
        // swap results, toolbar count and filter form content (keep drawer element so it stays open)
        ['[data-collection-results]', '.collection-toolbar'].forEach((sel) => {
          const a = this.querySelector(sel), b = fresh.querySelector(sel); if (a && b) a.replaceWith(b);
        });
        const formA = this.querySelector('[data-filters-form]'), formB = fresh.querySelector('[data-filters-form]');
        if (formA && formB) formA.innerHTML = formB.innerHTML;
        if (drawerOpen && !keepDrawer) Theme.drawer.close(drawerOpen);
        if (push) history.pushState({ boostlyCollection: true }, '', url.replace(/[?&]section_id=[^&]*/, ''));
      } catch (e) {
        window.location.href = url;
      } finally {
        this.classList.remove('is-loading');
        const r = this.querySelector('[data-collection-results]'); if (r) r.setAttribute('aria-busy', 'false');
      }
    }
  }
  define('boostly-collection-filters', CollectionFilters);
})();
